import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PackagePlus } from "lucide-react";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { BUNDLES } from "@/constants/testIds";

const BundleCard = ({ b, index }) => {
  const { addMany } = useCart();
  return (
    <article data-testid={BUNDLES.card(b.slug)} style={{ animationDelay: `${index * 90}ms` }} className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both bg-white border border-[#E7E0D3] rounded-[1.75rem] p-5 flex flex-col">
      <div className="flex -space-x-4">
        {b.products.map((p) => (
          <Link key={p.slug} to={`/product/${p.slug}`} title={p.name} className="w-20 h-20 rounded-2xl overflow-hidden border-4 border-white bg-[#F6F1E8] shadow-sm hover:-translate-y-1 transition-transform">
            <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
          </Link>
        ))}
      </div>
      <p className="mt-4 inline-flex w-fit px-2.5 py-1 rounded-full bg-[#E8F0EB] text-[#4E7B68] text-xs font-semibold">Save {Math.round(b.discount * 100)}%</p>
      <h3 className="mt-2 font-heading text-xl font-extrabold">{b.name}</h3>
      {b.tagline && <p className="mt-1 text-sm text-stone-600">{b.tagline}</p>}
      <ul className="mt-3 text-sm text-stone-600 space-y-0.5">{b.products.map((p) => <li key={p.slug}>· {p.name}</li>)}</ul>
      <div className="mt-auto pt-5 flex items-end justify-between gap-3">
        <p>
          <span data-testid={BUNDLES.price(b.slug)} className="font-heading text-2xl font-extrabold">{money(b.price)}</span>
          <span data-testid={BUNDLES.fullPrice(b.slug)} className="ml-2 text-sm text-stone-400 line-through">{money(b.full_price)}</span>
        </p>
        <button data-testid={BUNDLES.add(b.slug)} onClick={() => addMany(b.products, b.name)} className="min-h-[44px] inline-flex items-center gap-2 px-5 rounded-full bg-[#1C1917] text-white text-sm font-medium hover:bg-[#E06D44] active:scale-95 transition-[background-color,transform]">
          <PackagePlus size={16} /> Add bundle
        </button>
      </div>
    </article>
  );
};

export const KiddeeBundles = () => {
  const [bundles, setBundles] = useState([]);
  useEffect(() => { api.get("/bundles").then(({ data }) => setBundles(data)).catch(() => {}); }, []);
  if (!bundles.length) return null;
  return (
    <section data-testid={BUNDLES.section} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#4E7B68]">Kiddee bundles</p>
      <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">Sets she'd pack herself</h2>
      <p className="mt-3 max-w-2xl text-stone-600">Kiddee-picked combos at a bundle price. The saving shows up in your cart automatically (it doesn't combine with discount codes; you always get the bigger saving).</p>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {bundles.map((b, i) => <BundleCard key={b.slug} b={b} index={i} />)}
      </div>
    </section>
  );
};
